import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Users, ArrowRight, Loader2, Heart, X } from 'lucide-react';
import { campaigns as mockCampaigns } from '../mock';
import { api } from '../context/AuthContext';
import DonationModal from './DonationModal';
import { Button } from './ui/button';

const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

function CampaignCard({ c, onDonate }) {
  const isYako = c.type === 'Yako';
  const accent = isYako ? '#5a8268' : '#d97757';
  const pct = Math.min(100, Math.round(((c.raised || 0) / (c.goal || 1)) * 100));
  return (
    <Link to={`/cagnotte/${c.id}`} className="group block rounded-2xl bg-white border border-black/5 overflow-hidden card-lift">
      <div className="relative aspect-[16/10] overflow-hidden bg-neutral-200">
        <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" onError={(e) => { e.target.src = isYako ? 'https://images.unsplash.com/photo-1655720359248-eeace8c709c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200' : 'https://images.unsplash.com/photo-1647616927583-1d44a79a38a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200'; }} />
        <div className="absolute top-3 left-3">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md" style={{ background: accent, color: '#fff' }}>{c.type}</span>
        </div>
        <div className="absolute bottom-3 left-3 flex items-center gap-1 text-white text-[12px] drop-shadow">
          <MapPin className="w-3.5 h-3.5" /> {c.country}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-semibold text-[16px] text-[#1a1d1a] truncate">{c.title}</h3>
        <p className="mt-1 text-[13px] text-neutral-600 line-clamp-2">{c.description}</p>
        <div className="mt-4">
          <div className="h-1.5 rounded-full bg-neutral-200 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
          </div>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="text-[15px] font-semibold text-[#1a1d1a]">{formatN(c.raised || 0)} FCFA</div>
            <div className="text-[11px] text-neutral-500">sur {formatN(c.goal)} FCFA</div>
          </div>
          <div className="flex items-center gap-1 text-[12px] text-neutral-600">
            <Users className="w-3.5 h-3.5" /> {c.donors || 0}
          </div>
        </div>
        <Button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDonate(c); }} className="btn-anim mt-4 w-full h-10 rounded-full bg-[#5a8268] hover:bg-[#4a6f57] text-white text-[13.5px] font-medium">
          <Heart className="w-4 h-4 mr-1.5" /> Faire un don
        </Button>
      </div>
    </Link>
  );
}

export default function CampaignsSection({ reloadKey = 0 }) {
  const [params, setParams] = useSearchParams();
  const country = (params.get('country') || '').trim();
  const [items, setItems] = useState(mockCampaigns);
  const [loading, setLoading] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [innerReload, setInnerReload] = useState(0);

  const openDonate = (c) => { setSelected(c); setDonateOpen(true); };

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const requestParams = { limit: 12 };
        if (country) requestParams.country = country;
        const { data } = await api.get('/cagnottes', { params: requestParams });
        if (!alive) return;
        const backendItems = data.items || [];
        if (country) {
          // Backend already filtered — no fallback to mocks (they are all Côte d'Ivoire and would leak into other countries).
          setItems(backendItems);
        } else {
          const combined = [...backendItems, ...mockCampaigns].slice(0, 12);
          setItems(combined);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CampaignsSection] Impossible de charger les cagnottes, fallback sur les données mockées.', err);
        }
        if (alive) setItems(country ? [] : mockCampaigns);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [reloadKey, innerReload, country]);

  const clearCountry = () => {
    const next = new URLSearchParams(params);
    next.delete('country');
    setParams(next, { replace: false });
  };

  const heading = useMemo(() => (
    country ? (
      <>Cagnottes <span className="serif-italic text-[#d97757]">en {country}</span></>
    ) : (
      <>Cagnottes <span className="serif-italic text-[#d97757]">en cours</span></>
    )
  ), [country]);

  return (
    <section id="cagnottes" className="relative scroll-mt-24">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-neutral-500">En ce moment</div>
            <h2 className="mt-2 text-[clamp(2rem,4vw,3.4rem)] leading-[1.05] font-semibold tracking-tight text-[#1a1d1a]">
              {heading}
            </h2>
            {country && (
              <button
                type="button"
                onClick={clearCountry}
                data-testid="clear-country-filter"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#1a1d1a] text-white px-3 py-1 text-[12px] hover:bg-black transition-colors"
              >
                Filtre : {country} <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="hidden sm:inline-flex items-center gap-1.5 text-[13.5px] font-medium text-neutral-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>Cliquez pour donner</span> <ArrowRight className="w-4 h-4" /></>}
          </div>
        </div>
        {items.length === 0 && !loading ? (
          <div className="mt-8 rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center" data-testid="empty-country-state">
            <p className="text-[14.5px] text-neutral-700">Aucune cagnotte {country ? `en ${country}` : ''} pour le moment.</p>
            <p className="mt-1 text-[13px] text-neutral-500">Soyez le premier à en lancer une pour votre communauté.</p>
            <button
              type="button"
              onClick={clearCountry}
              className="mt-5 rounded-full bg-[#1a1d1a] text-white h-10 px-5 text-[13.5px] hover:bg-black"
            >
              Voir toutes les cagnottes
            </button>
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cagnottes-grid">
            {items.map(c => <CampaignCard key={c.id} c={c} onDonate={openDonate} />)}
          </div>
        )}
      </div>
      <DonationModal open={donateOpen} onOpenChange={setDonateOpen} cagnotte={selected} onDonated={() => { setInnerReload(k => k + 1); }} />
    </section>
  );
}
